import { useState , useMemo } from 'react';

import PropTypes from 'prop-types';
import { CloseOutlined, DeleteOutlined } from '@ant-design/icons';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import EditOutlined from '@ant-design/icons/EditOutlined';
import Checkbox from '@mui/material/Checkbox';

import Collapse from '@mui/material/Collapse';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

import IconButton from 'src/components/ui/IconButton';

const ActionPlate = (props) => {
	const {
		children,
		collapseNode,
		collapseLabel,
		isCollapsedProp,
		isCollapsedDefault,
		onCollapse,
		card,
		cardContent,
		isGrey,
		isPadded,
		topLines = [],
		onEdit,
		onDelete,
		onClose,
		checked,
		defaultChecked,
		onChange,
	} = props;

	const actions = useMemo(() => {
		return [onEdit, onDelete, onClose].filter((a) => !!a);
	}, [onEdit, onDelete, onClose]);

	const hasActions = actions.length > 0;

	const [isCollapsedState, setCollapsed] = useState(isCollapsedDefault);
	const isCollapsed =
		isCollapsedProp === undefined ? isCollapsedState : isCollapsedProp;
	const handleClickCollapse = () => {
		setCollapsed(!isCollapsed);
		if (onCollapse) onCollapse(!isCollapsed);
	};

	return (
		<Card
			variant="outlined"
			{...card}
			sx={{
				position: 'relative',
				backgroundColor: isGrey ? 'background.default' : 'background',
				pl: onChange ? 3 : 0,
				...card?.sx,
			}}
		>
			{onChange && (
				<Checkbox
					onChange={onChange}
					checked={checked}
					defaultChecked={defaultChecked}
					sx={{ position: 'absolute', left: 0, top: 0 }}
				/>
			)}
			{topLines.length > 0 && (
				<Stack
					direction="row"
					spacing={0.5}
					divider={<Typography color="secondary">·</Typography>}
					px={2}
					py={1}
					alignItems="center"
				>
					{topLines.map((line) => {
						return (
							<Typography
								key={line.id}
								color="secondary"
								variant="body2"
							>
								{line.text}
							</Typography>
						);
					})}
				</Stack>
			)}
			<CardContent
				{...cardContent}
				sx={{
					p: 2,
					pt: topLines.length ? 0.5 : 2,
					pr: isPadded ? 5.5 * actions.length : 2,
					...cardContent?.sx,
				}}
			>
				{children}
				{collapseNode && (
					<Stack spacing={2} mt={2}>
						<Typography
							component={Stack}
							color="secondary"
							alignItems="center"
							direction="row"
							onClick={handleClickCollapse}
							sx={{ alignSelf: 'start', cursor: 'pointer' }}
						>
							{collapseLabel}
							<KeyboardArrowDownIcon
								sx={{
									transform: isCollapsed && 'rotate(180deg)',
								}}
							/>
						</Typography>

						<Collapse in={isCollapsed} timeout="auto" unmountOnExit>
							{collapseNode}
						</Collapse>
					</Stack>
				)}
			</CardContent>
			{hasActions && (
				<Stack
					direction="row"
					sx={{ position: 'absolute', right: 8, top: 8 }}
				>
					{onEdit && (
						<IconButton shape="rounded" onClick={onEdit}>
							<EditOutlined />
						</IconButton>
					)}
					{onDelete && (
						<IconButton shape="rounded" onClick={onDelete}>
							<DeleteOutlined />
						</IconButton>
					)}
					{onClose && (
						<IconButton shape="rounded" onClick={onClose}>
							<CloseOutlined />
						</IconButton>
					)}
				</Stack>
			)}
		</Card>
	);
};

ActionPlate.propTypes = {
	children: PropTypes.oneOfType([
		PropTypes.arrayOf(PropTypes.node),
		PropTypes.node,
	]),
	collapseLabel: PropTypes.string,
	collapseNode: PropTypes.oneOfType([
		PropTypes.arrayOf(PropTypes.node),
		PropTypes.node,
	]),
	isCollapsedProp: PropTypes.bool,
	isCollapsedDefault: PropTypes.bool,
	onCollapse: PropTypes.func,
	topLines: PropTypes.arrayOf(
		PropTypes.shape({
			id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
			text: PropTypes.string,
		}),
	),
	card: PropTypes.shape(Card.propTypes),
	cardContent: PropTypes.shape(CardContent.propTypes),
	isGrey: PropTypes.bool,
	isPadded: PropTypes.bool,
	onEdit: PropTypes.func,
	onDelete: PropTypes.func,
	onClose: PropTypes.func,
	onChange: PropTypes.func,
	checked: PropTypes.bool,
	defaultChecked: PropTypes.bool,
};

export default ActionPlate;
